/**
 * Select, move and delete.
 *
 * Click picks the topmost entity under the cursor; drag on empty space rubber
 * bands; drag on a selected entity moves the whole selection.
 */

import type { CadDocument, Entity, LineEntity, PadEntity, Vertex } from '@sircad/core/model.ts';
import { LayerType, isElectrical, newId, padIsSchematic } from '@sircad/core/model.ts';
import { padThrough } from '@sircad/core/geom/pad.ts';
import { placeInComponent } from '@sircad/core/net/netlist.ts';
import type { Point } from '../render/viewport.ts';
import { orthoConstrain, snap } from '../render/viewport.ts';
import type { Tool, ToolContext } from './tool.ts';
import { entityBounds, hitTest, selectable } from './hit.ts';

type Mode = 'idle' | 'band' | 'move';

/** A trace end glued to a moving component's pad, dragged along with it. */
export interface Attached { line: LineEntity; end: 1 | 2; }

/** What a caller wants to hear about a carry it started. */
export interface CarryEnd {
  /**
   * Each step of the journey, as it happens.
   *
   * The total displacement at the end is not enough for anything that has to
   * stay alongside the geometry: a block turned halfway across the board was
   * moved, then turned, then moved again, and only the running total in order
   * reproduces that. The block outline is the caller that needs it.
   */
  move?(dx: number, dy: number): void;
  /** Put down, with the total displacement it travelled. */
  drop?(dx: number, dy: number): void;
  /** Escaped, tool switched, or otherwise given up on. */
  abandon?(): void;
}

export class SelectTool implements Tool {
  readonly id = 'select';
  readonly label = 'Select';
  readonly key = 's';
  readonly hint =
    'Click to grab and carry · click again to drop · drag to move · '
    + 'shift-click to add · Del to delete';
  readonly cursor = 'default';

  private mode: Mode = 'idle';
  /**
   * Carrying the selection: it follows the cursor with no button held.
   *
   * The original grabs this way — a click picks an item up, you move it with
   * the mouse *or* the arrow keys, and a second click puts it down. Press and
   * drag still works, but it is the lesser half: a drag cannot be steered with
   * the keyboard, and it forces you to hold the button for the whole journey
   * across a big board.
   */
  private held = false;
  private anchor: Point = { x: 0, y: 0 };
  /**
   * Displacement from {@link anchor} already applied to the selection.
   *
   * The move is tracked against where the grab started rather than the previous
   * event, because ortho snap has to constrain the *total* displacement. Judging
   * each small delta on its own would let a diagonal drag creep along both axes
   * a step at a time — the very thing ortho is there to prevent.
   */
  private applied: Point = { x: 0, y: 0 };
  private moved = false;
  /** Trace ends following the moving selection's pads, computed once per move. */
  private attached: Attached[] = [];
  /** Signal dots sitting on the moving geometry, which travel with it whole. */
  private dots: PadEntity[] = [];
  /**
   * Stubs grown on the pins this move leaves behind, kept so an empty one can
   * be taken away again when the wire is put back where it started.
   */
  private stubs: LineEntity[] = [];
  /**
   * The carry came from Block, which grows no stubs.
   *
   * The rule is `fgpoint`'s, and only Grab Line and Grab Vertex call that
   * (OGmenu03.cpp:115, 176). A block is attached to the cursor wholesale and
   * leaves nothing on the pins it passes.
   */
  private block = false;
  /**
   * Told how a carry ended, when the caller asked to be.
   *
   * Block Move wants `drop`: the marked region has to follow the geometry it
   * marks, or the block that was around your work is left behind on empty board
   * and the next Block command reports nothing inside it. Block Copy wants
   * `abandon`: its duplicates are made before the carry starts, so Escape has
   * to take them away again or it leaves a second copy stacked invisibly on the
   * first — which is what the original's own escape does, dropping the
   * attachments rather than setting them down.
   */
  private ending: CarryEnd | null = null;
  /**
   * Where the undo history stood when the grab took hold, so Escape can wind
   * the whole gesture back — the move and every turn, flip and scale in it.
   * Null when nothing is in hand.
   */
  private mark: number | null = null;

  onPointerDown(p: Point, ctx: ToolContext, ev: MouseEvent): void {
    // Carrying something? Then this click is where it goes. The geometry has
    // been moving with the cursor all along, so putting it down is simply
    // letting go — the undo snapshot was taken when it first moved.
    if (this.held) {
      this.held = false;
      this.mode = 'idle';
      this.moved = false;
      this.attached = [];
      this.dots = [];
      cullEmptyStubs(ctx.doc(), this.stubs);
      this.stubs = [];
      this.mark = null;
      const end = this.ending;
      this.ending = null;
      ctx.setMessage('Dropped');
      end?.drop?.(this.applied.x, this.applied.y);
      ctx.redraw();
      return;
    }

    this.anchor = p;
    this.applied = { x: 0, y: 0 };
    this.moved = false;
    this.block = false;
    // Before anything is touched, so Escape has a place to wind back to even if
    // the first thing that happens is an R rather than a move.
    this.mark = ctx.editMark();

    // A point from the keyboard crosshair carries no mouse event.
    const shift = ev?.shiftKey === true;

    // Grab what is on the layer being worked on: where copper stacks up, the
    // track you can see yourself editing is the one you mean to pick up. A
    // Grab-by-kind command narrows it further.
    const hit = hitTest(
      ctx.doc(), p.x, p.y, ctx.grid || 0.01, ctx.activeLayer, ctx.pickFilter ?? undefined);

    if (!hit) {
      if (!shift) ctx.selection.clear();
      // There is no band to drag out from a keyboard crosshair: nothing is
      // held down, so nothing would ever end it.
      this.mode = ev ? 'band' : 'idle';
      ctx.redraw();
      return;
    }

    // Grabbing something on another layer moves the working layer to it.
    //
    // The original's own first act once it has found the line: `if (es->pridx
    // != lyr) { lyr = es->pridx; setlayer(wp,lyr); }` (OGmenu03.cpp:92). It
    // reads as housekeeping and is not — picking a track up is how you say
    // which layer you are working on, and without it the next thing you draw
    // lands on the layer you were on before you went looking.
    if (ctx.doc().layers[hit.layer] && hit.layer !== ctx.activeLayer) {
      ctx.setActiveLayer(hit.layer);
    }

    // What "the line" means when a track bends: the whole run, not the one
    // straight piece under the cursor.
    const picked = ctx.grabChain && hit.kind === 'line'
      ? connectedRun(ctx.doc(), hit, ctx.pickFilter ?? undefined)
      : [hit];

    if (shift) {
      for (const e of picked) {
        if (ctx.selection.has(e.id)) ctx.selection.delete(e.id);
        else ctx.selection.add(e.id);
      }
    } else if (!ctx.selection.has(hit.id)) {
      ctx.selection.clear();
      for (const e of picked) ctx.selection.add(e.id);
    }

    // Dragging a selected item moves the whole selection.
    this.mode = ctx.selection.size > 0 ? 'move' : 'idle';

    // From the crosshair there is no release to wait for, so the grab takes
    // hold at once — which is what makes Grab work without a mouse at all.
    if (!ev && ctx.selection.size > 0) {
      this.held = true;
      ctx.setMessage(
        `Carrying ${ctx.selection.size} item${ctx.selection.size === 1 ? '' : 's'}`
        + ' — arrows move it, Space drops it, Esc puts it back');
    }
    ctx.redraw();
  }

  /**
   * Take hold of what is already selected, from a given point, without a click.
   *
   * Block Move is the caller. The original attaches every item in the block to
   * the graphics cursor the moment the command is given and then waits for one
   * point — the target — so the block is *carried* to where it goes and you can
   * see it land. Asking instead for a from-point and a to-point, which is what
   * this used to do, showed nothing in between and looked for all the world
   * like a command that had done nothing.
   *
   * Reaching the ordinary carry rather than inventing a second one is the whole
   * point: the arrow keys walk it, Escape puts it back, X, Y and R flip and
   * turn it, and none of that had to be written again for blocks.
   */
  carry(ctx: ToolContext, from: Point, ending?: CarryEnd): void {
    this.mode = 'move';
    this.held = true;
    this.anchor = from;
    this.applied = { x: 0, y: 0 };
    this.moved = false;
    this.attached = [];
    this.dots = [];
    this.stubs = [];
    this.block = true;
    this.ending = ending ?? null;
    this.mark = ctx.editMark();
    ctx.redraw();
  }

  onPointerMove(p: Point, ctx: ToolContext): void {
    if (this.mode === 'band') {
      ctx.preview.length = 0;
      ctx.preview.push(rectOutline(this.anchor, p, ctx.activeLayer));
      ctx.redraw();
      return;
    }

    if (this.mode !== 'move' && !this.held) return;

    const g = ctx.grid;
    // Where the cursor has taken the selection since the grab began.
    let wantX = snap(p.x, g) - snap(this.anchor.x, g);
    let wantY = snap(p.y, g) - snap(this.anchor.y, g);

    // Ortho snap holds the displacement to the directions the mode allows, in
    // ordinary use one axis only — the manual is explicit that a grabbed item
    // "can only be moved in the x direction or the y direction, but not both"
    // (ch.13, Ortho Snap mode), which is mode 1 and what `orthoConstrain` gives
    // for it. Judged on the total displacement, so changing your mind mid-drag
    // returns the other axis to the start rather than creeping along both.
    if (ctx.ortho) {
      const held = orthoConstrain(0, 0, wantX, wantY, ctx.ortho);
      wantX = held.x;
      wantY = held.y;
    }

    const dx = wantX - this.applied.x;
    const dy = wantY - this.applied.y;
    if (dx === 0 && dy === 0) return;

    if (!this.moved) {
      // Only take a snapshot once we know this is a move, not a bare click.
      ctx.beginEdit('Move');
      this.moved = true;
      // Find the traces glued to the moving components' pads, now — before
      // anything shifts — so they can be dragged along and stay connected.
      this.attached = collectAttached(ctx);
      this.dots = collectDots(ctx);
      // Last of the three, and it has to be: a stub is not selected and its
      // free end sits exactly on the moving wire's, so collecting it as a wire
      // to stretch would move it twice as far as the wire it belongs to.
      this.stubs = this.block ? [] : growStubs(ctx);
      for (const s of this.stubs) this.attached.push({ line: s, end: 2 });
    }

    for (const e of ctx.doc().entities) {
      if (ctx.selection.has(e.id)) translate(e, dx, dy);
    }
    // Rubber-band attached trace ends with the pads they sit on. The far end
    // stays put, so the segment stretches to keep the connection — routing as
    // best it can while you drag.
    for (const a of this.attached) {
      if (a.end === 1) { a.line.x1 += dx; a.line.y1 += dy; }
      else { a.line.x2 += dx; a.line.y2 += dy; }
    }
    // A dot is not stretched, it is carried: it marks a joint, and a joint that
    // stays behind while the wires move is a lie about the circuit.
    for (const dot of this.dots) { dot.x += dx; dot.y += dy; }
    this.applied = { x: wantX, y: wantY };
    this.ending?.move?.(dx, dy);
    ctx.commit();
  }

  onPointerUp(p: Point, ctx: ToolContext, ev: MouseEvent): void {
    // A keyboard grab already took hold on the way down; there is no release.
    if (this.held) return;

    if (this.mode === 'band') {
      const x1 = Math.min(this.anchor.x, p.x);
      const x2 = Math.max(this.anchor.x, p.x);
      const y1 = Math.min(this.anchor.y, p.y);
      const y2 = Math.max(this.anchor.y, p.y);

      // A band of essentially zero size is a click on empty space, not a
      // select-all-in-nothing.
      if (x2 - x1 > 1e-6 || y2 - y1 > 1e-6) {
        if (!ev.shiftKey) ctx.selection.clear();
        for (const e of entitiesInBand(
          ctx.doc(), { x1, y1, x2, y2 }, ctx.pickFilter ?? undefined)) {
          ctx.selection.add(e.id);
        }
      }
      ctx.preview.length = 0;
    }

    // A press and release that never moved is a click, and a click on something
    // picks it up rather than merely selecting it. The button is now free: the
    // selection follows the cursor, or the arrow keys, until it is put down.
    if (this.mode === 'move' && !this.moved && ctx.selection.size > 0) {
      this.held = true;
      this.anchor = p;
      this.applied = { x: 0, y: 0 };
      ctx.setMessage(
        `Carrying ${ctx.selection.size} item${ctx.selection.size === 1 ? '' : 's'}`
        + ' — click or Space to drop, Esc puts it back');
      ctx.redraw();
      return;
    }

    this.mode = 'idle';
    this.attached = [];
    cullEmptyStubs(ctx.doc(), this.stubs);
    this.stubs = [];
    ctx.redraw();
  }

  /** True while the selection is riding the cursor. */
  holding(): boolean {
    return this.held;
  }

  /**
   * Carrying something, or dragging a band.
   *
   * A selection sitting still is not busy: Escape should drop it and hand back
   * a plain cursor rather than needing a second press to do nothing.
   */
  busy(): boolean {
    return this.held || this.mode !== 'idle';
  }


  onKey(ev: KeyboardEvent, ctx: ToolContext): boolean {
    if (ev.key === 'Delete' || ev.key === 'Backspace') {
      if (ctx.selection.size === 0) return false;
      ctx.beginEdit('Delete');
      const d = ctx.doc();
      d.entities = d.entities.filter((e) => !ctx.selection.has(e.id));
      ctx.selection.clear();
      ctx.commit();
      return true;
    }

    // R, X, Y, M, A and K are not claimed here. The shell owns them (manual
    // ch.13, Shortcut Keys) and turns every kind properly; this tool's own R
    // rotated components and left the lines, arcs and text around them where
    // they were, which tore a mixed grab — a block, above all — in half. It ran
    // first and so was the one that counted.
    return false;
  }

  /**
   * Abandon whatever is in hand and put it back.
   *
   * Escape means "as you were" everywhere else, so a carried item returns to
   * where it was picked up rather than being dropped wherever it had got to.
   * The move has already been applied to the geometry — it follows the cursor
   * as you go — so putting it back is the same displacement in reverse, on the
   * selection and on the trace ends that came along with it.
   */
  cancel(ctx: ToolContext): void {
    // Wind the drawing back to where the grab began, whatever happened in
    // between. This used to reverse the displacement by arithmetic, which put
    // back a move and nothing else: R, X, Y, M, A and K all edit the geometry
    // in hand and none of them is a displacement, so a block turned on its way
    // across the board and then abandoned came back to its old position still
    // turned. Escape means "as you were", and only the history knows what that
    // was.
    if (this.mark !== null) ctx.revertTo(this.mark);
    this.mark = null;
    this.held = false;
    this.moved = false;
    this.applied = { x: 0, y: 0 };
    this.mode = 'idle';
    this.attached = [];
    this.dots = [];
    // The revert took the stubs with it — they were made after the mark.
    this.stubs = [];
    // Nothing was put down, so the caller is told it was given up on — which
    // is how Block Copy knows to take its duplicates away again.
    const end = this.ending;
    this.ending = null;
    end?.abandon?.();
    ctx.preview.length = 0;
    ctx.selection.clear();
    ctx.redraw();
  }
}

/**
 * Every segment joined end to end to this one.
 *
 * A polyline is not one object in this format — the writer emits a run of
 * separate line records, each starting where the last finished — so grabbing
 * "the line" at a bend has to follow the joins. It walks outward from both
 * ends of the segment picked, on the same layer, and stops where nothing meets
 * it. A junction where three or more segments meet takes them all: the run is
 * whatever is physically joined, which is what will move as one on the board.
 */
export function connectedRun(
  doc: CadDocument, start: LineEntity, filter?: (e: Entity) => boolean,
): LineEntity[] {
  const EPS = 1e-6;
  const same = (ax: number, ay: number, bx: number, by: number): boolean =>
    Math.abs(ax - bx) < EPS && Math.abs(ay - by) < EPS;

  const candidates: LineEntity[] = [];
  for (const e of doc.entities) {
    if (e.kind !== 'line' || e.layer !== start.layer) continue;
    if (!selectable(doc, e)) continue;
    if (filter && !filter(e)) continue;
    candidates.push(e);
  }

  const run = new Map<number, LineEntity>([[start.id, start]]);
  // Ends still to be followed, as (x, y) pairs.
  const open: { x: number; y: number }[] = [
    { x: start.x1, y: start.y1 }, { x: start.x2, y: start.y2 },
  ];

  while (open.length) {
    const at = open.pop()!;
    for (const e of candidates) {
      if (run.has(e.id)) continue;
      const head = same(e.x1, e.y1, at.x, at.y);
      const tail = same(e.x2, e.y2, at.x, at.y);
      if (!head && !tail) continue;
      run.set(e.id, e);
      // Follow on from the far end of whatever was just joined.
      open.push(head ? { x: e.x2, y: e.y2 } : { x: e.x1, y: e.y1 });
    }
  }
  return [...run.values()];
}

/**
 * Everything a rubber band takes.
 *
 * The layer rule belongs here as much as it belongs to a click, and it was
 * missing entirely: the band added whatever fell inside it on any layer at
 * all, hidden and locked included. Drag a box over a board with one layer
 * showing, press Delete, and every layer loses its contents — with nothing on
 * screen to say what went, because the rest was not being drawn.
 *
 * A separate function rather than a loop inside a pointer handler so the rule
 * can be tested without a mouse.
 */
export function entitiesInBand(
  doc: CadDocument, band: { x1: number; y1: number; x2: number; y2: number },
  filter?: (e: Entity) => boolean,
): Entity[] {
  const out: Entity[] = [];
  for (const e of doc.entities) {
    if (!selectable(doc, e)) continue;
    if (filter && !filter(e)) continue;
    const b = entityBounds(e);
    if (!b) continue;
    // Wholly inside, as the original does: a band takes what it encloses, not
    // what it grazes.
    if (b.x1 >= band.x1 && b.x2 <= band.x2
      && b.y1 >= band.y1 && b.y2 <= band.y2) out.push(e);
  }
  return out;
}

/**
 * Trace ends that sit on a moving component's pads. Computed before the move so
 * the pad positions are still their originals; the far end of each trace is
 * left alone, so dragging stretches the segment rather than detaching it.
 */
export function collectAttached(ctx: ToolContext): Attached[] {
  const doc = ctx.doc();
  /** Coincidence for a point that is not a pad — a wire end on a wire end. */
  const EPS = 1e-4;

  // Every point on the moving selection that a trace can be joined to, and how
  // near a trace end has to be to count as joined to it.
  //
  // A part's pads were the whole of this, so dragging a chip took its wiring
  // with it and dragging a track tore it away from the track it met — the same
  // move, two different answers. Anything a wire can end on belongs here: a
  // part's pads, a loose pad or via, and the ends of a moving segment.
  //
  // A pad's reach is half its smaller dimension, which is the original's own
  // figure — `w0 = min(sp.pad.x, sp.pad.y) * 0.5` in Grab Component
  // (OGmenu03.cpp:1005). Exact coincidence was the rule here, and it is the
  // wrong rule for a pad: a board need not agree with itself to the millionth
  // of an inch, and a track that stops a ten-thousandth short of the pad it
  // plainly lands on was left behind when the part moved. The same file that
  // taught the netlist this lesson — two segments 0.00064" apart reading as an
  // open — is the one that teaches it here.
  const pads: { x: number; y: number; r: number }[] = [];
  const padAt = (p: Vertex, pad: { sizeX: number; sizeY: number }): void => {
    pads.push({ x: p.x, y: p.y, r: Math.max(Math.min(pad.sizeX, pad.sizeY) * 0.5, EPS) });
  };
  for (const e of doc.entities) {
    if (!ctx.selection.has(e.id)) continue;
    if (e.kind === 'component') {
      for (const c of e.children) {
        if (c.kind === 'pad') padAt(placeInComponent({ x: c.x, y: c.y }, e), c);
      }
    } else if (e.kind === 'pad') {
      padAt({ x: e.x, y: e.y }, e);
    } else if (e.kind === 'line') {
      // A wire end is a point, not a pad: it has no width to be near.
      pads.push({ x: e.x1, y: e.y1, r: EPS }, { x: e.x2, y: e.y2, r: EPS });
    }
  }
  if (pads.length === 0) return [];

  const onPad = (x: number, y: number): boolean =>
    pads.some((p) => Math.hypot(p.x - x, p.y - y) <= p.r);

  const out: Attached[] = [];
  for (const e of doc.entities) {
    if (e.kind !== 'line' || ctx.selection.has(e.id)) continue;
    if (!conductive(doc, e.layer)) continue; // only real traces/wires follow
    // And nothing on a layer that may not be touched: stretching a trace you
    // cannot see is the same as moving one you cannot see.
    if (!selectable(doc, e)) continue;
    if (onPad(e.x1, e.y1)) out.push({ line: e, end: 1 });
    if (onPad(e.x2, e.y2)) out.push({ line: e, end: 2 });
  }
  return out;
}

/** True for a layer that carries connectivity — copper/plane on a board, a
 *  signal layer on a schematic. */
function conductive(doc: CadDocument, layer: number): boolean {
  const l = doc.layers[layer];
  if (!l) return false;
  return doc.kind === 'sch' ? l.type === LayerType.Schematic : isElectrical(l.type);
}

/**
 * The stub a wire leaves on a pin when it is dragged off it.
 *
 * `fgpoint` states its rule in three numbered cases at its head
 * (OGxyz12.cpp:2013) and this is the first: where the grabbed endpoint stands
 * on a **component pad**, the pad stays where it is and a *new zero-length
 * line* is made from the pad to the dragged end, which is then carried along
 * with it. So pulling a wire away from a pin grows a fresh segment rather than
 * tearing the wire off the pin — the rule that keeps a schematic connected
 * while it is being tidied. Cases 2 and 3 are the free pad and the plain line
 * ends, and are what stretching already did.
 *
 * The new segment takes the moving wire's layer, width, colour and signal,
 * which is what the original copies off the first line it grabs
 * (OGxyz12.cpp:2110).
 *
 * Reach is the pad's — half its smaller dimension, the figure
 * {@link collectAttached} already uses to decide a wire end is on a pad. The
 * original asks a tighter question here, a box of an eighth the line width,
 * but the two directions of one gesture have to agree: drag the part and keep
 * the wire, drag the wire and lose the pin is not a rule, it is a
 * disagreement.
 *
 * Returns null when nothing under the point is a pin.
 */
export function stubOnPin(
  doc: CadDocument, x: number, y: number, like: LineEntity,
  moving?: ReadonlySet<number>,
): LineEntity | null {
  for (const e of doc.entities) {
    if (e.kind !== 'component') continue;
    // A pad that is moving too needs no stub: the wire end goes with it.
    if (moving?.has(e.id)) continue;
    if (!selectable(doc, e)) continue;
    for (const c of e.children) {
      if (c.kind !== 'pad') continue;
      // Off-layer surface pads are not there to be landed on, which is the
      // original's own bypass (`fs->pridt & 4`); a through pad answers on any
      // layer, and a schematic pin is a through type so the rule never bites
      // on a sheet.
      if (!padThrough(c) && c.layer !== like.layer) continue;
      const at = placeInComponent({ x: c.x, y: c.y }, e);
      const reach = Math.max(Math.min(c.sizeX, c.sizeY) * 0.5, 1e-4);
      if (Math.hypot(at.x - x, at.y - y) > reach) continue;
      const stub: LineEntity = {
        kind: 'line', id: newId(), layer: like.layer, width: like.width,
        // Pad first: X1Y1 is the end that stays. The original writes the pad's
        // own point into both, which is the same thing whenever the wire ends
        // exactly on the pad and a hair short of connected when it does not —
        // so the free end is put where the wire actually is, as the function's
        // own header says it should be ("from the pad to the attached line
        // endpoint").
        x1: at.x, y1: at.y, x2: x, y2: y,
      };
      if (like.color !== undefined) stub.color = like.color;
      if (like.signal !== undefined) stub.signal = like.signal;
      return stub;
    }
  }
  return null;
}

/**
 * Grow a stub on every pin the moving wires are about to leave, and put them
 * in the drawing.
 *
 * Each is anchored at its pin by X1Y1; carry X2Y2 with the move — which is
 * what the returned list is attached by — and the wire stays joined to the pin
 * it came off.
 */
export function growStubs(ctx: ToolContext): LineEntity[] {
  const doc = ctx.doc();
  const made: LineEntity[] = [];
  // One stub per point per layer. A run grabbed whole meets itself at its
  // bends, and two stubs on one pin is one wire drawn twice.
  const seen = new Set<string>();
  for (const e of doc.entities) {
    if (e.kind !== 'line' || !ctx.selection.has(e.id)) continue;
    if (!conductive(doc, e.layer)) continue;
    if (!selectable(doc, e)) continue;
    for (const end of [1, 2] as const) {
      const x = end === 1 ? e.x1 : e.x2;
      const y = end === 1 ? e.y1 : e.y2;
      const key = `${e.layer},${x},${y}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const stub = stubOnPin(doc, x, y, e, ctx.selection);
      if (stub) made.push(stub);
    }
  }
  doc.entities.push(...made);
  return made;
}

/**
 * Take away the stubs that never grew.
 *
 * A wire put back where it came from leaves a zero-length line on the pin,
 * which is junk in the file. The original culls a line that comes out shorter
 * than a hundredth of its own width when the cursor is set down
 * (`linelength(es) < (linewidth(es)*0.01)`, OGxyz13.cpp:117); it applies that
 * to the lines it grabbed rather than the ones it made, which is the one place
 * this goes further than the original and does so for the reason the original
 * states.
 */
export function cullEmptyStubs(doc: CadDocument, stubs: readonly LineEntity[]): void {
  const dead = new Set<number>();
  for (const s of stubs) {
    if (Math.hypot(s.x2 - s.x1, s.y2 - s.y1) <= s.width * 0.01) dead.add(s.id);
  }
  if (dead.size) doc.entities = doc.entities.filter((e) => !dead.has(e.id));
}

/** Move an entity's geometry by a delta, in place. */
export function translate(e: Entity, dx: number, dy: number): void {
  switch (e.kind) {
    case 'line':
      e.x1 += dx; e.y1 += dy; e.x2 += dx; e.y2 += dy;
      break;
    case 'arc':
    case 'spiral':
      e.cx += dx; e.cy += dy;
      break;
    case 'pad':
    case 'text':
    case 'component':
      e.x += dx; e.y += dy;
      break;
    case 'fill':
      e.bounds.x1 += dx; e.bounds.x2 += dx;
      e.bounds.y1 += dy; e.bounds.y2 += dy;
      for (const v of e.vertices) { v.x += dx; v.y += dy; }
      break;
  }
}

/** A closed rectangle used as the rubber-band preview. */
function rectOutline(a: Point, b: Point, layer: number): Entity {
  const vertices: Vertex[] = [
    { x: a.x, y: a.y }, { x: b.x, y: a.y },
    { x: b.x, y: b.y }, { x: a.x, y: b.y },
  ];
  return {
    kind: 'fill',
    id: -1,
    layer,
    // A non-zero width makes the renderer outline rather than flood it.
    width: 0.001,
    bounds: {
      x1: Math.min(a.x, b.x), y1: Math.min(a.y, b.y),
      x2: Math.max(a.x, b.x), y2: Math.max(a.y, b.y),
    },
    vertices,
  };
}

/**
 * Signal dots sitting on the geometry about to move.
 *
 * A dot is a statement that wires join here. Move the wires and leave the dot
 * behind and the drawing now says the joint is somewhere it is not — so it
 * travels, whole, rather than stretching the way a trace end does.
 *
 * Only schematic-type pads (`T0`, `S3`) that stand on their own: a pad inside a
 * component moves with its component already, and a via on a board is not a
 * dot. And only ones nothing else is holding — a dot already in the selection
 * is moved by the main pass, and moving it twice would send it double.
 */
export function collectDots(ctx: ToolContext): PadEntity[] {
  const doc = ctx.doc();
  const EPS = 1e-4;

  const points: Vertex[] = [];
  for (const e of doc.entities) {
    if (!ctx.selection.has(e.id)) continue;
    if (e.kind === 'component') {
      for (const c of e.children) {
        if (c.kind === 'pad') points.push(placeInComponent({ x: c.x, y: c.y }, e));
      }
    } else if (e.kind === 'pad') {
      points.push({ x: e.x, y: e.y });
    } else if (e.kind === 'line') {
      points.push({ x: e.x1, y: e.y1 }, { x: e.x2, y: e.y2 });
    }
  }
  if (points.length === 0) return [];

  const out: PadEntity[] = [];
  for (const e of doc.entities) {
    if (e.kind !== 'pad' || ctx.selection.has(e.id)) continue;
    if (!padIsSchematic(e.type)) continue;
    if (!selectable(doc, e)) continue;
    if (points.some((p) => Math.abs(p.x - e.x) < EPS && Math.abs(p.y - e.y) < EPS)) {
      out.push(e);
    }
  }
  return out;
}
